import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ErrorFallbackProps {
  error: Error;
  resetError?: () => void;
}

/**
 * User-friendly error fallback component
 * Displayed when the application encounters an error
 */
export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 px-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
        <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
          <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
        </div>

        <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-2">
          Oops! Something went wrong
        </h1>

        <p className="text-center text-gray-600 dark:text-gray-400 mb-6">
          We're sorry for the inconvenience. The error has been reported to our team.
        </p>

        {/* Show error details in development */}
        {import.meta.env.DEV && (
          <div className="mb-6 p-4 bg-gray-100 dark:bg-gray-900 rounded-lg">
            <p className="text-sm font-mono text-red-600 dark:text-red-400 break-words">
              {error.message}
            </p>
            {error.stack && (
              <details className="mt-2">
                <summary className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
                  Stack trace
                </summary>
                <pre className="text-xs text-gray-600 dark:text-gray-400 mt-2 overflow-x-auto">
                  {error.stack}
                </pre>
              </details>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3">
          {resetError && (
            <Button onClick={resetError} className="w-full" size="lg">
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          )}

          <Button
            onClick={() => (window.location.href = "/")}
            variant="outline"
            className="w-full"
            size="lg"
          >
            Go to Homepage
          </Button>
        </div>

        <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-6">
          Error ID: {Date.now().toString(36)}
        </p>
      </div>
    </div>
  );
}
